/* eslint-disable no-useless-escape */

import playerEmbedSrc from '@/utils/config/playerEmbedSrc';

/**
 * Gets hash for script.
 *
 * @param    {string} playerType  Player type.
 * @param    {object} player      Player instance.
 * @param    {object} show        Show instance
 * @returns  {string}             Hash.
 */
const getHash = (playerType, player, show) => 'archive' === playerType ? player.hash : show.hash;

/**
 * Get title for iframe.
 *
 * @param   {string}  playerType  Player type.
 * @param   {object}  player      Player instance.
 * @param   {object}  show        Show instance
 * @returns {string}              iframe title.
 */
const getIframeTitle = (playerType, player, show) => `${show.title}${
    'archive' !== playerType ? ` - ${player.tracks[0].title}` : ''
}`;

/**
 * Gets URL for iframe.
 *
 * @param   {string}  url         Application URL.
 * @param   {string}  playerType  Player type.
 * @param   {object}  player      Player instance.
 * @param   {object}  show        Show instance
 * @param   {number}  trackNo     Track number
 * @param   {number|null}  trackGuid     Track GUID.
 * @returns {string}              iframe URL.
 */
const getIframeUrl = (url, playerType, player, show, trackNo, trackGuid = null) =>
    `${url}/embed/player/${playerType}/${getHash(playerType, player, show)}/${
        'archive' !== playerType ? (trackGuid ?? trackNo) : ''
    }?uncontained`;

/**
 * Gets script HTML tag with appropriate attributes.
 *
 * @param   {string}  playerType  Player type.
 * @param   {object}  player      Player instance.
 * @param   {object}  show        Show instance.
 * @param   {number}  trackNo     Track number.
 * @param   {number|null}  trackGuid     Track GUID.
 * @returns {string}              oEmbed script HTML tag.
 */
export const getEmbedScriptTag = (playerType, player, show, trackNo, trackGuid = null) => {
    if (trackGuid) {
        return `\<script async data-type="${playerType}"${
            'archive' === playerType ? ` data-hash="${getHash(playerType, player, show)}"` : ''
        }${
            'archive' !== playerType ? ` data-track="${trackGuid}"` : ''
        }${
            'production' !== process.env.MIX_APP_ENV ? ' data-dev="true"' : ''
        } src="${playerEmbedSrc[process.env.MIX_APP_ENV]}" \>\<\/script\>`;
    }

    return `\<script async data-type="${playerType}" data-hash="${getHash(playerType, player, show)}"${
        'archive' !== playerType ? ` data-track="${trackNo}"` : ''
    } src="${playerEmbedSrc[process.env.MIX_APP_ENV]}"${
        'production' !== process.env.MIX_APP_ENV ? ' data-dev="true"' : ''
    }\>\<\/script\>`;
};

/**
 * Gets iframe HTML tag with appropriate attributes.
 *
 * @param   {string}  url         Application URL.
 * @param   {string}  playerType  Player type.
 * @param   {object}  player      Player instance.
 * @param   {object}  show        Show instance
 * @param   {number}  trackNo     Track number
 * @param   {number|null}  trackGuid     Track GUID.
 * @returns {string}              iframe HTML tag.
 */
export const getEmbedIframeTag = (url, playerType, player, show, trackNo, trackGuid = null) =>
    `\<iframe src="${getIframeUrl(url, playerType, player, show, trackNo, trackGuid)}" frameborder="0" width="100%" height="${
        'archive' === playerType ? 590 : 160
    }" title="${getIframeTitle(playerType, player, show)}"\>\</iframe\>`;
